/**
 * The three authentication endpoints, and nothing else.
 *
 * Requesting a code deliberately reveals nothing about whether the address is
 * known — the API answers the same way either way — so the form always advances
 * to the code step on success.
 */

import { otpVerifyResponseSchema } from '@pbtv/shared';
import type { z } from 'zod';
import { apiRequest, requestJson } from './api.js';

export type SignedInSession = z.infer<typeof otpVerifyResponseSchema>;

/**
 * `turnstileToken` comes from the Cloudflare Turnstile widget on the login form.
 * The API checks it before it will spend an SES send, and each token is good for
 * exactly one request, so the widget is reset after every attempt rather than
 * after failures alone.
 */
export async function requestOtp(email: string, turnstileToken: string): Promise<void> {
  await requestJson('/auth/otp/request', {
    method: 'POST',
    anonymous: true,
    body: { email, turnstileToken },
  });
}

export async function verifyOtp(email: string, code: string): Promise<SignedInSession> {
  return apiRequest(otpVerifyResponseSchema, '/auth/otp/verify', {
    method: 'POST',
    anonymous: true,
    body: { email, code },
  });
}

/** Revokes the session server-side. The caller clears local state regardless. */
export async function signOutRemote(): Promise<void> {
  await requestJson('/auth/logout', { method: 'POST' });
}
